import React, {useEffect} from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import 'remixicon/fonts/remixicon.css'
import {QueryClientProvider} from "@tanstack/react-query";
import {clearQueryCacheOnSignOut, queryClient} from "./api/reactQuery.ts";
import {auth} from "./firebaseApp.ts";
import * as Sentry from "@sentry/react";
import {createRoutesFromChildren, matchRoutes, useLocation, useNavigationType,} from "react-router-dom";
import {UnauthenticatedBackendApi} from "./api/UnauthenticatedApi.ts";
import {BannerProvider} from "./contexts/BannerContext.tsx";
import {BuildEnvironment, getBuildEnvironment} from "./lib/buildEnvironment.ts";

const buildEnvironment = getBuildEnvironment();

if (buildEnvironment === BuildEnvironment.PROD || buildEnvironment === BuildEnvironment.STAGING) {
    Sentry.init({
        dsn: "https://11d90da8cb5442d9f285b243088f6c2d@o4507706999963648.ingest.de.sentry.io/4508572586213456",
        integrations: [
            // See docs for support of different versions of variation of react router
            // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
                networkDetailAllowUrls: [window.location.origin, 'monorepo', /.*/],
            }),
        ],
        environment: buildEnvironment,

        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
    });
}

declare global {
    interface Window {
        hj: any;
        gtag: (...args: any[]) => void;
    }
}


auth.onAuthStateChanged(clearQueryCacheOnSignOut);

interface Theme {
    name: string;
    logoURL: string;
    iconURL: string;
    colors: {
        primary: string;
        liteprimary: string;
        superliteprimary: string;
    }
}

interface Partner {
    id: string,
    name: string,
    slug: string,
    tagline: string,
    address: string,
    email: string,
    phone_number: string,
    primary_color: string,
    full_logo_url: string,
    icon_logo_url: string,
    website_url: string,
    created_at: string,
    updated_at: string,
    business_id: string,
}

function getThemeFromPartner(currentPartner: Partner): Theme {
    const partnerPrimaryColor = "#" + currentPartner.primary_color;

    return {
        name: currentPartner.name,
        logoURL: currentPartner.full_logo_url,
        iconURL: currentPartner.icon_logo_url,
        colors: {
            primary: partnerPrimaryColor,
            liteprimary: `color-mix(in srgb,  ${partnerPrimaryColor}, white 20%)`,
            superliteprimary: `color-mix(in srgb,  ${partnerPrimaryColor}, white 75%)`,
        }
    }
}


function updateTheme(theme: Theme) {

    document.documentElement.style.setProperty('--primary', theme.colors.primary);
    document.documentElement.style.setProperty('--liteprimary', theme.colors.liteprimary);
    document.documentElement.style.setProperty('--superliteprimary', theme.colors.superliteprimary);

    globalThis.theme = {
        logoURL: theme.logoURL,
        theme: theme,
    }
}


async function setupApp() {
    const publicAPI = new UnauthenticatedBackendApi();
    const currentPartner = await publicAPI.Partner__findByHostUrl(window.location.hostname);
    let params = new URLSearchParams(document.location.search);

    if (currentPartner.result && !params.get("nbr")) {
        const theme = getThemeFromPartner(currentPartner.result)

        updateTheme(theme)
        const favicon = document.querySelector('#favicon')
        if (!favicon)
            throw new Error('Could not update favicon')

        favicon.setAttribute('href', theme.iconURL);
        document.title = theme.name + ' | PocketCFO'
        globalThis.isWhiteLabeled = true;


    } else {
        updateTheme({
            name: 'PocketCFO',
            logoURL: "",
            iconURL: "",
            colors: {
                primary: "#45AEEA",
                liteprimary: "#5CB2E2",
                superliteprimary: "rgba(201,225,246,0.54)",
            }
        })
        globalThis.isWhiteLabeled = false;
    }

    if (params.get("nbr")) globalThis.isWhiteLabeled = true;

    ReactDOM.createRoot(document.getElementById('root')!).render(
        <React.StrictMode>
            <QueryClientProvider client={queryClient}>
                <BannerProvider>
                    <App/>
                </BannerProvider>
            </QueryClientProvider>
        </React.StrictMode>,
    )
}

setupApp()
